import clsx from 'clsx';
import { useExperiment } from '@hungryroot/experiments';
import { Coin2 } from '@hungryroot/icons';
import { Paragraph, Heading } from '@hungryroot/ramen';
import styles from './PointsWithIcon.module.css';

export enum PointsWithIconVariant {
  Default = 'default',
  Large = 'large',
  Removed = 'removed',
}

interface Props {
  testid?: string;
  points: string;
  ariaLabel?: string;
  variant?: PointsWithIconVariant;
  className?: string;
  containerClassName?: string;
  discount?: number | null;
  pointsAfterDiscount?: number | null;
  discountedPriceFirst?: boolean;
}

export function PointsWithIcon(props: Props) {
  const {
    ariaLabel,
    testid,
    points,
    variant = PointsWithIconVariant.Default,
    className,
    containerClassName,
    discount,
    pointsAfterDiscount,
    discountedPriceFirst = true,
  } = props;

  const itemLevelDiscount = useExperiment(203);
  const shouldShowDiscount =
    itemLevelDiscount !== undefined && itemLevelDiscount > 0;

  const pointsContent = (
    <>
      <Coin2 />
      <span
        data-testid={`${testid}`}
        className={className}
        aria-label={ariaLabel}
      >
        {points === 'FREE' ? 'FREE' : parseFloat(points)}
      </span>
    </>
  );

  const discountedPriceElement = (
    <span className={clsx(styles.container, styles['red'])}>
      <Coin2 />
      <span
        data-testid={`${testid}-discounted`}
        className={className}
        aria-label={`Discounted to ${pointsAfterDiscount} points`}
      >
        {pointsAfterDiscount?.toString()}
      </span>
    </span>
  );

  const originalPriceElement = (
    <span className={styles.removed}>
      <Coin2 />
      <span
        data-testid={`${testid}-original`}
        aria-label={`Original price ${points} points`}
      >
        {parseFloat(points)}
      </span>
    </span>
  );

  // Design calls for discounted and original price to be shown in different orders in various places
  const discountedContent = discount && pointsAfterDiscount && (
    <>
      {discountedPriceFirst ? (
        <>
          {discountedPriceElement}
          {originalPriceElement}
        </>
      ) : (
        <>
          {originalPriceElement}
          {discountedPriceElement}
        </>
      )}
    </>
  );

  if (variant === PointsWithIconVariant.Default) {
    return (
      <span className={styles.container}>
        {discount && shouldShowDiscount ? discountedContent : pointsContent}
      </span>
    );
  }

  if (variant === PointsWithIconVariant.Large) {
    return (
      <Heading
        className={`${styles.large} ${containerClassName}`}
        variant="H6"
        testid={`${testid}-large`}
      >
        {discount && shouldShowDiscount ? discountedContent : pointsContent}
      </Heading>
    );
  }

  return (
    <Paragraph
      aria-label={ariaLabel}
      className={styles.removed}
      variant="caption"
      testid={`${testid}-removed`}
    >
      {pointsContent}
    </Paragraph>
  );
}
